import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { ButtonBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// ----------------------------------------------------------------------
// The control every grid-toolbar action is built from (Cabinet Base
// ToolbarButton): toolbar-control-height tall, radius-toolbar-control
// corners, a divider border on paper.
//
//   rest    → hover   primary tint 6  + primary border
//   active  (a filter or non-default setting applies) primary tint 12
//   open    (its menu is showing)                     primary tint 18
//
// Highlighted, the label turns `primary.softText` and icons `primary.main`.
// Focus is the theme's shared ring (ButtonBase).
// ----------------------------------------------------------------------

const StyledControl = styled(ButtonBase, {
    shouldForwardProp: (prop) =>
        !['ownerActive', 'ownerOpen', 'ownerIconOnly', 'ownerFullWidth'].includes(prop)
})(({ theme, ownerActive, ownerOpen, ownerIconOnly, ownerFullWidth }) => {
    const { primary } = theme.palette;
    const highlighted = ownerActive || ownerOpen;

    return {
        'display': 'inline-flex',
        'alignItems': 'center',
        'justifyContent': ownerIconOnly ? 'center' : 'space-between',
        'gap': theme.spacing(1),
        'flexShrink': 0,
        'height': theme.sizes.toolbarControlHeight,
        'minWidth': ownerIconOnly ? theme.sizes.toolbarControlHeight : 0,
        'width': ownerFullWidth ? '100%' : 'auto',
        'padding': ownerIconOnly ? theme.spacing(0, 1) : '0 14px 0 12px',
        'border': `1px solid ${highlighted ? primary.border : theme.palette.divider}`,
        'borderRadius': theme.shape.toolbarControl,
        'backgroundColor': theme.palette.background.paper,
        'color': highlighted ? primary.softText : theme.palette.text.primary,
        ...theme.typography.toolbarButton,
        'whiteSpace': 'nowrap',
        'transition': theme.transitions.create(
            ['background-color', 'border-color', 'color'],
            { duration: theme.transitions.duration.shortest }
        ),
        ...(ownerActive && { backgroundColor: primary.tint12 }),
        ...(ownerOpen && { backgroundColor: primary.tint18 }),
        '& svg': {
            width: 18,
            height: 18,
            flexShrink: 0,
            ...(highlighted && { color: primary.main })
        },
        '&:hover': {
            borderColor: primary.border,
            ...(!highlighted && { backgroundColor: primary.tint6 })
        },
        '&.Mui-disabled': {
            color: theme.palette.text.disabled,
            backgroundColor: theme.palette.action.disabledBackground
        }
    };
});

const Arrow = styled(ArrowDropDownIcon, {
    shouldForwardProp: (prop) => prop !== 'ownerOpen'
})(({ ownerOpen }) => ({
    transition: 'transform 160ms ease',
    transform: ownerOpen ? 'rotate(180deg)' : 'none'
}));

const ToolbarDropdownButton = forwardRef(
    (
        {
            label,
            icon,
            open = false,
            active = false,
            iconOnly = false,
            fullWidth = false,
            arrow = !iconOnly,
            ...other
        },
        ref
    ) => (
        <StyledControl
            ref={ref}
            ownerActive={active}
            ownerOpen={open}
            ownerIconOnly={iconOnly}
            ownerFullWidth={fullWidth}
            aria-expanded={arrow ? open : undefined}
            {...other}
        >
            {icon}
            {!iconOnly && label && <span>{label}</span>}
            {arrow && <Arrow ownerOpen={open} />}
        </StyledControl>
    )
);

ToolbarDropdownButton.propTypes = {
    /** Visible text; with `iconOnly`, pass `aria-label` instead. */
    label: PropTypes.node,
    icon: PropTypes.node,
    /** Its menu is showing. */
    open: PropTypes.bool,
    /** A filter or a non-default setting applies. */
    active: PropTypes.bool,
    iconOnly: PropTypes.bool,
    fullWidth: PropTypes.bool,
    /** Show the dropdown arrow; on by default unless `iconOnly`. */
    arrow: PropTypes.bool
};

export default ToolbarDropdownButton;
